/**
 * /login — single-step authentication.
 *
 * Submits username + password and, on success, navigates to the dashboard.
 */
import { useNavigate } from '@tanstack/react-router';
import { useState, type FormEvent } from 'react';
import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { FormField, Input } from '@/components/ui/form-field';
import { LoginLayout } from '@/components/login-layout';
import { useLoginMutation } from '@/lib/auth';
import { usePageTitle } from '@/lib/use-page-title';

export function LoginPage() {
  usePageTitle('Sign in');
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const login = useLoginMutation();

  const onSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    if (!username || !password) return;
    login.mutate(
      { username, password },
      {
        onSuccess: () => {
          void navigate({ to: '/' });
        },
      },
    );
  };

  return (
    <LoginLayout
      title="Sign in"
      subtitle="Enter your credentials to continue."
      footer="Locked accounts? Speak to your administrator."
    >
      <form onSubmit={onSubmit}>
        {login.error ? (
          <Alert tone="danger" className="mb-4">
            {login.error.message}
          </Alert>
        ) : null}
        <FormField label="Username" required htmlFor="username">
          <Input
            id="username"
            type="text"
            autoComplete="username"
            autoFocus
            value={username}
            onChange={(event) => setUsername(event.target.value)}
            invalid={Boolean(login.error)}
          />
        </FormField>
        <FormField label="Password" required htmlFor="password">
          <Input
            id="password"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            invalid={Boolean(login.error)}
          />
        </FormField>
        <Button
          type="submit"
          variant="primary"
          size="form"
          className="w-full"
          loading={login.isPending}
          disabled={!username || !password}
        >
          Sign in
        </Button>
      </form>
    </LoginLayout>
  );
}
